import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { StickyNote, Send, Plus, Trash2, MessageSquare, Bot, User, Loader2, Upload, Download, Eye, RotateCcw, CheckSquare, Square, Calendar as CalendarIcon, Check, Repeat, BarChart3, ArrowRightLeft, Package, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { getUser } from '../lib/auth';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SpreidingstoolTab from './SpreidingstoolPage';
import { EXACT_LOGO } from '../lib/logos';

/* ─── HELPERS ─── */
function fmtEur(n) {
  return n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function extractJsonBlock(text) {
  const match = text.match(/```json\s*([\s\S]*?)```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed.type === 'boeking' && parsed.regels && Array.isArray(parsed.regels)) {
      return parsed;
    }
  } catch (e) { console.warn('[Memo] JSON parse failed:', e.message); }
  return null;
}

function getTextWithoutJson(text) {
  return text.replace(/```json[\s\S]*?```/, '').trim();
}

/* ─── MARKDOWN RENDERER ─── */
function ChatMarkdown({ content }) {
  return (
    <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-headings:text-[#0b1220] prose-headings:mb-2 prose-headings:mt-3 prose-p:mb-2 prose-table:text-xs">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children }) => (<div className="overflow-x-auto my-2"><table className="w-full border-collapse text-xs font-mono">{children}</table></div>),
          thead: ({ children }) => <thead className="bg-[#003366]/5">{children}</thead>,
          th: ({ children }) => <th className="border border-gray-200 px-2 py-1.5 text-left font-semibold text-[#003366]">{children}</th>,
          td: ({ children }) => <td className="border border-gray-200 px-2 py-1.5">{children}</td>,
          strong: ({ children }) => <strong className="font-bold text-[#0b1220]">{children}</strong>,
          h2: ({ children }) => <h2 className="text-base font-bold text-[#003366] mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-bold text-[#0b1220] mt-2 mb-1">{children}</h3>,
          ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
          li: ({ children }) => <li className="text-sm">{children}</li>,
          hr: () => <hr className="my-3 border-gray-200" />,
          p: ({ children }) => <p className="mb-1.5">{children}</p>,
          code: ({ children, className }) => {
            if (className) return null; // skip code blocks (we handle json separately)
            return <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
          },
          pre: () => null, // skip pre blocks (json handled separately)
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/* ─── BOOKING PREVIEW CARD ─── */
function BookingPreview({ boeking, onApprove, approving }) {
  const [herhaling, setHerhaling] = useState(boeking.herhaling || 1);
  const isJaar = herhaling === 12;

  const maanden = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

  // Generate rows for all months if herhaling=12
  const allRegels = useMemo(() => {
    if (herhaling === 1) return boeking.regels;
    const rows = [];
    const startPeriode = parseInt(boeking.periode) || 1;
    for (let m = startPeriode; m <= 12; m++) {
      const mndLabel = maanden[m - 1];
      const jrLabel = String(boeking.jaar).slice(-2);
      const periodeStr = `${mndLabel}-${jrLabel}`;
      boeking.regels.forEach(r => {
        const omschr = r.omschrijving.replace(/[a-z]{3}-\d{2}/g, periodeStr);
        rows.push({ ...r, omschrijving: omschr, _periode: m });
      });
    }
    return rows;
  }, [boeking, herhaling, maanden]);

  const totDebet = allRegels.reduce((s, r) => s + (r.debet || 0), 0);
  const totCredit = allRegels.reduce((s, r) => s + (r.credit || 0), 0);

  return (
    <div className="mt-3 rounded-xl border border-[#003366]/20 bg-[#003366]/[0.02] overflow-hidden" data-testid="ai-booking-preview">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#003366]/10 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-[#003366]">
            {boeking.entiteitA} → {boeking.entiteitB}
          </p>
          <p className="text-[10px] text-[#526071] mt-0.5">
            {boeking.kostentype} &bull; &euro;{fmtEur(boeking.bedrag)} &bull; DB90 &bull; {boeking.boekingsdatum}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-herhaling-btn"
            onClick={() => setHerhaling(prev => prev === 1 ? 12 : 1)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${
              isJaar ? 'bg-[#003366] text-white' : 'bg-gray-100 text-[#526071] hover:bg-gray-200'
            }`}
          >
            <Repeat className="w-3 h-3" />
            {isJaar ? 'Heel jaar (jan-dec)' : 'Eenmalig'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
        <table className="w-full tw1-table">
          <thead className="sticky top-0 bg-white">
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left">Entiteit</th>
              <th className="px-3 py-2 text-left">Dagboek</th>
              <th className="px-3 py-2 text-left">Grootboek</th>
              <th className="px-3 py-2 text-left">Omschrijving</th>
              <th className="px-3 py-2 text-right">Debet</th>
              <th className="px-3 py-2 text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {allRegels.map((r, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-xs text-[#0b1220]">{r.entiteit}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-[#526071]">{r.dagboek}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-[#003366]">{r.grootboek}</td>
                <td className="px-3 py-1.5 text-xs text-[#0b1220]">{r.omschrijving}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-right">
                  {r.debet > 0 ? <span className="text-green-600">{fmtEur(r.debet)}</span> : ''}
                </td>
                <td className="px-3 py-1.5 text-xs font-mono text-right">
                  {r.credit > 0 ? <span className="text-red-600">{fmtEur(r.credit)}</span> : ''}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-[#003366]">
              <td colSpan={4} className="px-3 py-1.5 text-xs font-semibold">TOTAAL ({allRegels.length} regels)</td>
              <td className="px-3 py-1.5 text-xs font-mono font-bold text-right text-green-600">{fmtEur(Math.round(totDebet * 100) / 100)}</td>
              <td className="px-3 py-1.5 text-xs font-mono font-bold text-right text-red-600">{fmtEur(Math.round(totCredit * 100) / 100)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Approve button */}
      <div className="px-4 py-3 border-t border-[#003366]/10 flex justify-end">
        <Button
          data-testid="approve-download-btn"
          onClick={() => onApprove(boeking, allRegels, herhaling)}
          disabled={approving}
          className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-xs"
          size="sm"
        >
          {approving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
          {approving ? 'Exporteren...' : `Goedkeuren en exporteren`}
        </Button>
      </div>
    </div>
  );
}

/* ─── AI MEMO TAB ─── */
function AiMemoTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `session-${Date.now()}`);
  const [sessions, setSessions] = useState([]);
  const [approving, setApproving] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };
  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadSessions = useCallback(async () => {
    try { const res = await api.get('/api/memo/sessions'); setSessions(res.data.sessions || []); }
    catch (err) { console.error('[Memo] Sessions laden mislukt:', err); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadHistory = async (sid) => {
    setSessionId(sid);
    try { const res = await api.get(`/api/memo/history/${sid}`); setMessages(res.data.messages || []); }
    catch (err) { console.error('[Memo] History laden mislukt:', err); }
  };

  const newSession = () => { setSessionId(`session-${Date.now()}`); setMessages([]); };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try { await api.delete(`/api/memo/history/${sid}`); if (sid === sessionId) newSession(); loadSessions(); }
    catch (err) { console.error('[Memo] Sessie verwijderen mislukt:', err); }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim(); setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await api.post('/api/memo/chat', { message: userMsg, session_id: sessionId, history: messages.slice(-10) });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      loadSessions();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const status = err.response?.status;
      let errorMsg;
      if (detail.includes('EMERGENT_LLM_KEY')) {
        errorMsg = '**AI niet beschikbaar:** De API-sleutel is niet geconfigureerd. Neem contact op met de beheerder om de EMERGENT_LLM_KEY in te stellen.';
      } else if (detail.includes('library')) {
        errorMsg = '**AI niet beschikbaar:** De AI-library is niet geïnstalleerd op de server. Vraag de beheerder om `emergentintegrations` te installeren.';
      } else if (status === 401) {
        errorMsg = '**Sessie verlopen:** Je sessie is verlopen. Log opnieuw in om de chatbot te gebruiken.';
      } else if (status === 429) {
        errorMsg = '**Te veel verzoeken:** De AI-service is tijdelijk overbelast. Wacht een moment en probeer het opnieuw.';
      } else if (detail.includes('budget') || detail.includes('quota') || detail.includes('insufficient')) {
        errorMsg = '**AI-budget bereikt:** Het tegoed voor de AI-service is op. Ga naar Profiel → Universal Key → Saldo Opwaarderen om meer tegoed toe te voegen.';
      } else if (detail.includes('timeout') || detail.includes('timed out')) {
        errorMsg = '**Time-out:** Het AI-antwoord duurde te lang. Probeer een kortere vraag of probeer het later opnieuw.';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        errorMsg = '**Verbindingsfout:** Kan de server niet bereiken. Controleer je internetverbinding en probeer het opnieuw.';
      } else if (status >= 500) {
        errorMsg = `**Serverfout:** Er is een interne fout opgetreden. ${detail ? `Details: ${detail}` : 'Probeer het later opnieuw.'}`;
      } else {
        errorMsg = `**Fout:** ${detail || 'Er is een onverwachte fout opgetreden. Probeer het opnieuw.'}`;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, isError: true }]);
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const downloadFile = (file) => {
    const bytes = Uint8Array.from(atob(file.content_b64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadZip = (zipB64, filename) => {
    const bytes = Uint8Array.from(atob(zipB64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleApprove = async (boeking, regels, herhaling) => {
    setApproving(true);
    setExportResult(null);
    try {
      const exportRows = regels.map(r => ({
        datum: boeking.boekingsdatum || '',
        grootboek: r.grootboek || '',
        bedrag: r.debet > 0 ? r.debet : -(r.credit || 0),
        omschrijving: r.omschrijving || '',
        entiteit: r.entiteit || '',
      }));
      const herhalingLabel = herhaling === 12 ? 'jaar' : 'eenmalig';
      const filename = `TW1_Memo_${boeking.entiteitA.replace(/\s/g, '')}_${boeking.entiteitB.replace(/\s/g, '')}_${herhalingLabel}.xls`;
      const res = await api.post('/api/memo/export', {
        rows: exportRows,
        filename,
        metadata: {
          entiteitA: boeking.entiteitA,
          entiteitB: boeking.entiteitB,
          kostentype: boeking.kostentype,
          bedrag: boeking.bedrag,
          herhaling,
          regels: regels.length,
        },
      });
      setExportResult({ files: res.data.files, zip_b64: res.data.zip_b64, filename });
      toast.success(`${res.data.files.length} bestand(en) gegenereerd en gelogd`);
    } catch (err) {
      toast.error('Export mislukt');
    } finally { setApproving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" style={{ height: 'calc(100vh - 280px)' }}>
      {/* Sidebar: Sessions */}
      <div className="hidden lg:block">
        <div className="tw1-card p-4 h-full overflow-y-auto">
          <h3 className="font-semibold text-sm text-[#0b1220] mb-3">Gesprekken</h3>
          <div className="space-y-1">
            <button onClick={newSession} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#526071] hover:bg-gray-50 transition-colors" data-testid="new-session-btn">
              <Plus className="w-4 h-4" /> Nieuw gesprek
            </button>
            {sessions.map((s, i) => {
              const lastMsg = s.messages?.[0]?.content || 'Leeg gesprek';
              const isActive = s.session_id === sessionId;
              return (
                <button key={i} onClick={() => loadHistory(s.session_id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors group ${isActive ? 'bg-[#003366]/5 text-[#003366]' : 'text-[#526071] hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate text-left">{lastMsg.substring(0, 35)}</span>
                  </div>
                  <span role="button" onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity cursor-pointer">
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div className="lg:col-span-3 flex flex-col">
        <div className="tw1-card flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-[#003366]/5 flex items-center justify-center mx-auto mb-4"><Bot className="w-8 h-8 text-[#003366]" /></div>
                <h3 className="text-lg font-bold text-[#0b1220] mb-2">TW1 Memo Assistent</h3>
                <p className="text-sm text-[#526071] max-w-md mx-auto mb-6">
                  Beschrijf de doorbelasting die je wilt boeken. Ik genereer de memoriaalboekingen (dagboek 90) voor beide entiteiten.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
                  {[
                    'Holding betaalt verzekering voor Rotterdam, \u20ac2.500 in maart 2026',
                    'Doorbelasting schoonmaakkosten van Eindhoven naar Holten, \u20ac800 in februari',
                    'IT-kosten Group verdelen over Aalsmeer en Utrecht, elk \u20ac1.200 in januari',
                    'Huurkosten Maasvlakte doorbelasten naar Holding, \u20ac3.000 maart'
                  ].map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} className="text-left px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#526071] hover:border-[#f5a800] hover:bg-[#f5a800]/5 transition-colors" data-testid={`suggestion-${i}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => {
              const isUser = msg.role === 'user';
              const isError = msg.isError;
              const jsonBoeking = !isUser ? extractJsonBlock(msg.content) : null;
              const textContent = !isUser && jsonBoeking ? getTextWithoutJson(msg.content) : msg.content;

              return (
                <div key={i}>
                  <div className={`flex gap-3 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${isError ? 'bg-red-100' : 'bg-[#003366]/10'}`}>
                        <Bot className={`w-4 h-4 ${isError ? 'text-red-600' : 'text-[#003366]'}`} />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      isUser ? 'bg-[#003366] text-white rounded-br-md' : isError ? 'bg-red-50 border border-red-200 text-[#0b1220] rounded-bl-md' : 'bg-gray-50 text-[#0b1220] rounded-bl-md'
                    }`}>
                      {isUser ? (
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{textContent}</div>
                      ) : (
                        <ChatMarkdown content={textContent} />
                      )}
                    </div>
                    {isUser && (
                      <div className="w-8 h-8 rounded-lg bg-[#f5a800]/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-4 h-4 text-[#f5a800]" />
                      </div>
                    )}
                  </div>
                  {/* Booking preview */}
                  {jsonBoeking && (
                    <div className="ml-11 mr-11">
                      <BookingPreview boeking={jsonBoeking} onApprove={handleApprove} approving={approving} />
                    </div>
                  )}
                </div>
              );
            })}

            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#003366]" />
                </div>
                <div className="bg-gray-50 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#003366] animate-spin" />
                  <span className="text-sm text-[#526071]">Boeking genereren...</span>
                </div>
              </div>
            )}

            {/* Export result panel */}
            {exportResult && (
              <div className="ml-11 mr-4" data-testid="memo-export-result">
                <div className="rounded-xl border border-green-200 bg-green-50/50 p-4">
                  <h4 className="font-semibold text-sm text-[#1e8c45] mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4" /> {exportResult.files.length} bestand(en) gegenereerd
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    {exportResult.files.map((f, i) => (
                      <button key={i} onClick={() => downloadFile(f)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-[#003366]/30 hover:bg-[#003366]/[0.02] transition-colors text-left"
                        data-testid={`memo-download-${i}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                          <FileDown className="w-4 h-4 text-[#003366]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#0b1220] truncate">{f.filename}</p>
                          <p className="text-[10px] text-[#526071]">{f.entiteit}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {exportResult.zip_b64 && (
                    <button
                      onClick={() => downloadZip(exportResult.zip_b64, exportResult.filename.replace('.xls', '.zip'))}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#003366] text-white text-xs font-medium hover:bg-[#00244d] transition-colors"
                      data-testid="memo-download-zip"
                    >
                      <Package className="w-3.5 h-3.5" /> Download alles als .zip
                    </button>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-4">
            <div className="flex gap-2">
              <Input data-testid="memo-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Beschrijf de doorbelasting..." disabled={loading} className="h-11" />
              <Button data-testid="memo-chat-send" onClick={sendMessage} disabled={!input.trim() || loading} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white h-11 px-4">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-[#526071] mt-2">Dagboek 90 (Memoriaal) &bull; Beschrijf wie betaalt, wie de kosten draagt, welke kosten, welke maand en het bedrag</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── TEGENBOEKING TAB (unchanged) ─── */
function TegenboekingTab() {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [tegenDatum, setTegenDatum] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (f) => {
    if (!f) return;
    setFile(f); setLoading(true); setError(''); setParsed(null); setSelected(new Set()); setShowPreview(false);
    try {
      const fd = new FormData(); fd.append('file', f);
      const res = await api.post('/api/tegenboeking/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setParsed(res.data);
      setSelected(new Set(res.data.rows.map((_, i) => i)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Verwerking mislukt');
    } finally { setLoading(false); }
  };

  const clear = () => { setFile(null); setParsed(null); setError(''); setSelected(new Set()); setShowPreview(false); if (fileRef.current) fileRef.current.value = ''; };

  const toggleRow = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const selectAll = () => { if (parsed) setSelected(new Set(parsed.rows.map((_, i) => i))); };
  const deselectAll = () => { setSelected(new Set()); };

  const selectedRows = parsed ? parsed.rows.filter((_, i) => selected.has(i)) : [];
  const tegenRegels = selectedRows.map(r => ({
    grootboek: r.grootboek, omschrijving: r.omschrijving,
    debet: r.credit, credit: r.debet, kostenplaats: r.kostenplaats,
    datum: r.datum || '',
  }));
  const tegenDebet = tegenRegels.reduce((s, r) => s + r.debet, 0);
  const tegenCredit = tegenRegels.reduce((s, r) => s + r.credit, 0);

  const handleExport = async () => {
    if (tegenRegels.length === 0) return;
    setExporting(true);
    try {
      // Strip grootboek name: "4050 - Huur" → "4050"
      const stripGB = (gb) => { const idx = (gb || '').indexOf(' - '); return idx > 0 ? gb.substring(0, idx).trim() : (gb || '').trim(); };
      // Use datum from source file, fallback to manual input or today
      const fallbackDatum = tegenDatum || new Date().toISOString().slice(0, 10);
      const fallbackParts = fallbackDatum.split('-');
      const fallbackFormatted = fallbackParts.length === 3 ? `${fallbackParts[2]}-${fallbackParts[1]}-${fallbackParts[0]}` : fallbackDatum;
      const exportRows = tegenRegels.map(r => {
        // Format datum from source: could be YYYY-MM-DD or DD-MM-YYYY
        let datumFormatted = fallbackFormatted;
        if (r.datum) {
          const d = r.datum.trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
            const p = d.split('-');
            datumFormatted = `${p[2]}-${p[1]}-${p[0]}`;
          } else if (/^\d{2}-\d{2}-\d{4}$/.test(d)) {
            datumFormatted = d;
          } else {
            datumFormatted = d;
          }
        }
        return {
          datum: datumFormatted,
          grootboek: stripGB(r.grootboek),
          bedrag: r.debet > 0 ? r.debet : -(r.credit || 0),
          omschrijving: `TEGENBOEKING ${r.omschrijving || ''}`,
        };
      });
      const datumStr = tegenDatum || new Date().toISOString().slice(0, 10);
      const filename = `Tegenboeking_${datumStr.replace(/-/g, '')}.xls`;
      const res = await api.post('/api/tegenboeking/export', {
        rows: exportRows, filename,
        metadata: { bron_bestand: file?.name || '', datum_tegenboeking: datumStr, regels: tegenRegels.length, totaal_debet: Math.round(tegenDebet * 100) / 100, totaal_credit: Math.round(tegenCredit * 100) / 100 },
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} gedownload en gelogd`);
    } catch (err) { toast.error('Export mislukt'); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <div data-testid="tegen-upload-dropzone" className="tw1-card p-10 text-center cursor-pointer hover:border-[#003366]/40 transition-colors" onClick={() => fileRef.current?.click()}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} data-testid="tegen-file-input" />
          <Upload className="w-10 h-10 text-[#526071] mx-auto mb-3" />
          <p className="text-sm font-medium text-[#0b1220]">Upload boekstuk-export (.xlsx)</p>
          <p className="text-xs text-[#526071] mt-1">Export uit Exact Online (dagboek 90)</p>
        </div>
      ) : (
        <div className="tw1-card p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#003366]/10 flex items-center justify-center"><StickyNote className="w-4 h-4 text-[#003366]" /></div>
            <div>
              <p className="text-sm font-medium text-[#0b1220]">{file.name}</p>
              <p className="text-xs text-[#526071]">{(file.size / 1024).toFixed(1)} KB{parsed ? ` \u2022 ${parsed.total_rows} regels gevonden` : ''}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={clear} data-testid="tegen-clear-btn"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}

      {loading && <div className="tw1-card p-8 text-center"><div className="animate-spin w-8 h-8 border-3 border-[#003366] border-t-transparent rounded-full mx-auto" /></div>}
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-[#c0392b]" data-testid="tegen-error">{error}</div>}

      {parsed && parsed.rows.length > 0 && (
        <>
          {(parsed.metadata.administratie || parsed.metadata.dagboek || parsed.metadata.datum) && (
            <div className="tw1-card p-4">
              <p className="text-xs font-semibold text-[#526071] uppercase tracking-wide mb-2">Metadata uit bestand</p>
              <div className="flex flex-wrap gap-4 text-xs">
                {parsed.metadata.administratie && <span><span className="text-[#526071]">Administratie:</span> <span className="font-mono font-medium">{parsed.metadata.administratie}</span></span>}
                {parsed.metadata.dagboek && <span><span className="text-[#526071]">Dagboek:</span> <span className="font-mono font-medium">{parsed.metadata.dagboek}</span></span>}
                {parsed.metadata.periode && <span><span className="text-[#526071]">Periode:</span> <span className="font-mono font-medium">{parsed.metadata.periode}</span></span>}
                {parsed.metadata.datum && <span><span className="text-[#526071]">Datum:</span> <span className="font-mono font-medium">{parsed.metadata.datum}</span></span>}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={selectAll} data-testid="tegen-select-all-btn"><CheckSquare className="w-3.5 h-3.5 mr-1" /> Selecteer alles</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={deselectAll} data-testid="tegen-deselect-all-btn"><Square className="w-3.5 h-3.5 mr-1" /> Deselecteer alles</Button>
              <span className="text-xs text-[#526071]">{selected.size} van {parsed.rows.length} geselecteerd</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-3.5 h-3.5 text-[#526071]" />
                <label className="text-xs text-[#526071]">Datum tegenboeking:</label>
                <Input data-testid="tegen-datum-input" type="date" value={tegenDatum} onChange={e => setTegenDatum(e.target.value)} className="h-8 w-40 text-xs font-mono" />
              </div>
            </div>
          </div>

          <div className="tw1-card overflow-hidden" data-testid="tegen-source-table">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-[#0b1220]">Bronregels — selecteer regels voor tegenboeking</h3>
              <p className="text-xs text-[#526071] mt-0.5">{parsed.total_rows} regels &bull; Debet: &euro;{fmtEur(parsed.total_debet)} &bull; Credit: &euro;{fmtEur(parsed.total_credit)}</p>
            </div>
            <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
              <table className="w-full tw1-table">
                <thead className="sticky top-0 bg-white z-10"><tr className="bg-gray-50">
                  <th className="px-3 py-2.5 w-10"></th>
                  <th className="px-3 py-2.5 text-left">Grootboek</th>
                  <th className="px-3 py-2.5 text-left">Omschrijving</th>
                  <th className="px-3 py-2.5 text-right">Debet</th>
                  <th className="px-3 py-2.5 text-right">Credit</th>
                  <th className="px-3 py-2.5 text-left">Kostenplaats</th>
                </tr></thead>
                <tbody>
                  {parsed.rows.map((r, i) => (
                    <tr key={i} className={`cursor-pointer ${selected.has(i) ? 'bg-[#003366]/[0.03]' : ''}`} onClick={() => toggleRow(i)}>
                      <td className="px-3 py-2 text-center"><Checkbox checked={selected.has(i)} onCheckedChange={() => toggleRow(i)} data-testid={`tegen-row-check-${i}`} /></td>
                      <td className="px-3 py-2 text-sm font-mono text-[#003366]">{r.grootboek}</td>
                      <td className="px-3 py-2 text-sm text-[#0b1220]">{r.omschrijving}</td>
                      <td className="px-3 py-2 text-sm font-mono text-right">{r.debet > 0 ? <span className="text-green-600">{fmtEur(r.debet)}</span> : ''}</td>
                      <td className="px-3 py-2 text-sm font-mono text-right">{r.credit > 0 ? <span className="text-red-600">{fmtEur(r.credit)}</span> : ''}</td>
                      <td className="px-3 py-2 text-sm font-mono text-[#526071]">{r.kostenplaats}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selected.size > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowPreview(!showPreview)} data-testid="tegen-toggle-preview-btn">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> {showPreview ? 'Verberg tegenboeking' : 'Toon tegenboeking'}
                </Button>
                <Button onClick={handleExport} disabled={exporting} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-xs" size="sm" data-testid="tegen-export-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> {exporting ? 'Exporteren...' : `Download .xls (${tegenRegels.length} regels)`}
                </Button>
              </div>
              {showPreview && (
                <div className="tw1-card overflow-hidden border-l-4 border-l-[#003366]" data-testid="tegen-preview-table">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-[#003366]" /><h3 className="text-sm font-semibold text-[#0b1220]">Tegenboeking preview</h3></div>
                    <p className="text-xs text-[#526071] mt-0.5">{tegenRegels.length} omgekeerde regels</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full tw1-table">
                      <thead><tr className="bg-gray-50">
                        <th className="px-4 py-2.5 text-left">Grootboek</th><th className="px-4 py-2.5 text-left">Omschrijving</th>
                        <th className="px-4 py-2.5 text-right">Debet</th><th className="px-4 py-2.5 text-right">Credit</th><th className="px-4 py-2.5 text-left">Kostenplaats</th>
                      </tr></thead>
                      <tbody>
                        {tegenRegels.map((r, i) => (
                          <tr key={i}>
                            <td className="px-4 py-2 text-sm font-mono text-[#003366]">{r.grootboek}</td>
                            <td className="px-4 py-2 text-sm">{r.omschrijving}</td>
                            <td className="px-4 py-2 text-sm font-mono text-right">{r.debet > 0 ? <span className="text-green-600">{fmtEur(r.debet)}</span> : ''}</td>
                            <td className="px-4 py-2 text-sm font-mono text-right">{r.credit > 0 ? <span className="text-red-600">{fmtEur(r.credit)}</span> : ''}</td>
                            <td className="px-4 py-2 text-sm font-mono text-[#526071]">{r.kostenplaats}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-[#003366]">
                          <td colSpan={2} className="px-4 py-2 text-xs font-semibold">TOTAAL</td>
                          <td className="px-4 py-2 text-xs font-mono font-bold text-right text-green-600">{fmtEur(Math.round(tegenDebet * 100) / 100)}</td>
                          <td className="px-4 py-2 text-xs font-mono font-bold text-right text-red-600">{fmtEur(Math.round(tegenCredit * 100) / 100)}</td>
                          <td className="px-4 py-2">{Math.round(tegenDebet * 100) === Math.round(tegenCredit * 100) && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700">SLUITEND</span>}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!file && !loading && (
        <div className="tw1-card p-12 text-center" data-testid="tegen-empty-state">
          <RotateCcw className="w-10 h-10 mx-auto mb-3 text-[#003366]" style={{ opacity: 0.3 }} />
          <p className="text-sm font-medium text-[#0b1220]">Upload een boekstuk-export uit Exact Online</p>
          <p className="text-xs text-[#526071] mt-1">Het systeem leest de grootboekrekeningen en genereert een omgekeerde boeking</p>
        </div>
      )}
    </div>
  );
}

/* ─── IC CORRECTIE TAB ─── */
/* IC Correctie Tab — Dynamische kostenrekeningen uit gl_accounts */

function IcCorrectieTab() {
  const [entities, setEntities] = useState([]);
  const [betaler, setBetaler] = useState('');
  const [ontvanger, setOntvanger] = useState('');
  const [bedrag, setBedrag] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [kostenrekening, setKostenrekening] = useState('');
  const [kostenAccounts, setKostenAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [periode, setPeriode] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [datum, setDatum] = useState(() => {
    const n = new Date();
    const last = new Date(n.getFullYear(), n.getMonth() + 1, 0);
    return last.toISOString().split('T')[0];
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/ic-correctie/entities').then(res => setEntities(res.data.entities || [])).catch(() => {});
  }, []);

  // Load dynamic cost accounts when ontvanger changes
  useEffect(() => {
    if (!ontvanger) { setKostenAccounts([]); setKostenrekening(''); return; }
    setLoadingAccounts(true);
    api.get(`/api/ic-correctie/accounts/${encodeURIComponent(ontvanger)}`)
      .then(res => {
        const accts = res.data.accounts || [];
        setKostenAccounts(accts);
        setKostenrekening(prev => {
          if (accts.length > 0 && !accts.find(a => a.code === prev)) return accts[0].code;
          return prev;
        });
      })
      .catch(() => setKostenAccounts([]))
      .finally(() => setLoadingAccounts(false));
  }, [ontvanger]);

  const handleGenerate = async () => {
    setError('');
    if (!betaler || !ontvanger || !bedrag || !omschrijving) {
      setError('Vul alle verplichte velden in'); return;
    }
    if (betaler === ontvanger) {
      setError('Betaler en ontvanger mogen niet dezelfde zijn'); return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/ic-correctie/generate', {
        betaler, ontvanger, bedrag: parseFloat(bedrag.replace(',', '.')),
        omschrijving, kostenrekening, periode, datum,
      });
      setResult(res.data);
      toast.success('IC Correctie boekingen gegenereerd');
    } catch (err) {
      setError(err.response?.data?.detail || 'Genereren mislukt');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (file) => {
    const bytes = Uint8Array.from(atob(file.content_b64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const rcBetaler = entities.find(e => e.naam === betaler)?.rc || '';
  const rcOntvanger = entities.find(e => e.naam === ontvanger)?.rc || '';
  const parsedBedrag = parseFloat((bedrag || '0').replace(',', '.')) || 0;
  const kortBetaler = betaler.replace('TW1 ', '').replace(' BV', '');
  const kortOntvanger = ontvanger.replace('TW1 ', '').replace(' BV', '');

  return (
    <div className="space-y-6">
      <div className="tw1-card p-6">
        <h3 className="font-semibold text-sm text-[#0b1220] mb-1 flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-[#003366]" /> Intercompany Correctie
        </h3>
        <p className="text-xs text-[#526071] mb-5">
          Corrigeer facturen die bij de verkeerde entiteit zijn geboekt. Genereert twee .xls bestanden (één per entiteit).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[#526071]">Betalende entiteit (heeft betaald)</Label>
            <Select value={betaler} onValueChange={setBetaler}>
              <SelectTrigger className="h-10" data-testid="ic-betaler-select"><SelectValue placeholder="Selecteer entiteit..." /></SelectTrigger>
              <SelectContent>
                {entities.map(e => <SelectItem key={e.naam} value={e.naam}>{e.naam} <span className="text-[10px] text-[#526071] ml-1">({e.rc})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[#526071]">Ontvangende entiteit (moet kosten dragen)</Label>
            <Select value={ontvanger} onValueChange={setOntvanger}>
              <SelectTrigger className="h-10" data-testid="ic-ontvanger-select"><SelectValue placeholder="Selecteer entiteit..." /></SelectTrigger>
              <SelectContent>
                {entities.map(e => <SelectItem key={e.naam} value={e.naam}>{e.naam} <span className="text-[10px] text-[#526071] ml-1">({e.rc})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[#526071]">Bedrag</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#526071]">&euro;</span>
              <Input data-testid="ic-bedrag-input" value={bedrag} onChange={e => setBedrag(e.target.value)} placeholder="1.490,99" className="h-10 pl-8 font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[#526071]">Kostenrekening {ontvanger && <span className="text-[10px] text-[#003366]">({ontvanger})</span>}</Label>
            <Select value={kostenrekening} onValueChange={setKostenrekening} disabled={!ontvanger || loadingAccounts}>
              <SelectTrigger className="h-10" data-testid="ic-kosten-select">
                <SelectValue placeholder={loadingAccounts ? 'Laden...' : !ontvanger ? 'Selecteer eerst ontvangende entiteit' : 'Selecteer rekening...'} />
              </SelectTrigger>
              <SelectContent>
                {kostenAccounts.map(k => <SelectItem key={k.code} value={k.code}>{k.code} — {k.description}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[#526071]">Omschrijving</Label>
            <Input data-testid="ic-omschrijving-input" value={omschrijving} onChange={e => setOmschrijving(e.target.value)} placeholder="Onderhoudskosten installatie" className="h-10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#526071]">Periode</Label>
              <Input type="month" value={periode} onChange={e => setPeriode(e.target.value)} className="h-10 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#526071]">Factuurdatum</Label>
              <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} className="h-10 text-sm" />
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-[#c0392b] mt-3">{error}</p>}

        <Button data-testid="ic-generate-btn" onClick={handleGenerate} disabled={loading} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white mt-5" size="sm">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1.5" />}
          Genereer IC Correctie
        </Button>
      </div>

      {/* Preview */}
      {betaler && ontvanger && betaler !== ontvanger && parsedBedrag > 0 && (
        <div className="tw1-card overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <h4 className="text-xs font-semibold text-[#003366] uppercase tracking-wide">Preview boekingsregels</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full tw1-table text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">Entiteit</th>
                  <th className="px-3 py-2 text-left">Dagboek</th>
                  <th className="px-3 py-2 text-left">Grootboek</th>
                  <th className="px-3 py-2 text-left">Omschrijving</th>
                  <th className="px-3 py-2 text-right">Debet</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-blue-50/30">
                  <td className="px-3 py-2 font-medium" colSpan={6}>Boeking 1 — {betaler}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2">{betaler}</td>
                  <td className="px-3 py-2 font-mono">90</td>
                  <td className="px-3 py-2 font-mono text-[#003366]">{rcOntvanger}</td>
                  <td className="px-3 py-2">IC correctie: {omschrijving || '...'} &rarr; {kortOntvanger}</td>
                  <td className="px-3 py-2 text-right font-mono text-green-600">{fmtEur(parsedBedrag)}</td>
                  <td className="px-3 py-2 text-right font-mono"></td>
                </tr>
                <tr>
                  <td className="px-3 py-2">{betaler}</td>
                  <td className="px-3 py-2 font-mono">90</td>
                  <td className="px-3 py-2 font-mono text-[#003366]">{kostenrekening}</td>
                  <td className="px-3 py-2">IC correctie: {omschrijving || '...'} &rarr; {kortOntvanger}</td>
                  <td className="px-3 py-2 text-right font-mono"></td>
                  <td className="px-3 py-2 text-right font-mono text-red-600">{fmtEur(parsedBedrag)}</td>
                </tr>
                <tr className="bg-blue-50/30">
                  <td className="px-3 py-2 font-medium" colSpan={6}>Boeking 2 — {ontvanger}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2">{ontvanger}</td>
                  <td className="px-3 py-2 font-mono">90</td>
                  <td className="px-3 py-2 font-mono text-[#003366]">{kostenrekening}</td>
                  <td className="px-3 py-2">IC correctie: {omschrijving || '...'} &larr; {kortBetaler}</td>
                  <td className="px-3 py-2 text-right font-mono text-green-600">{fmtEur(parsedBedrag)}</td>
                  <td className="px-3 py-2 text-right font-mono"></td>
                </tr>
                <tr>
                  <td className="px-3 py-2">{ontvanger}</td>
                  <td className="px-3 py-2 font-mono">90</td>
                  <td className="px-3 py-2 font-mono text-[#003366]">{rcBetaler}</td>
                  <td className="px-3 py-2">IC correctie: {omschrijving || '...'} &larr; {kortBetaler}</td>
                  <td className="px-3 py-2 text-right font-mono"></td>
                  <td className="px-3 py-2 text-right font-mono text-red-600">{fmtEur(parsedBedrag)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Download results */}
      {result && (
        <div className="tw1-card p-5" data-testid="ic-result">
          <h4 className="font-semibold text-sm text-[#1e8c45] mb-3 flex items-center gap-2">
            <Check className="w-4 h-4" /> IC Correctie gegenereerd
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {result.files.map((f, i) => (
              <button key={i} onClick={() => downloadFile(f)}
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-[#003366]/30 hover:bg-[#003366]/[0.02] transition-colors text-left"
                data-testid={`ic-download-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5 text-[#003366]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0b1220] truncate">{f.filename}</p>
                  <p className="text-xs text-[#526071]">{f.entiteit}</p>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#526071] mt-3">Beide bestanden zijn gelogd in het Logboek onder module "IC Correctie"</p>
        </div>
      )}
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function Tw1MemoPage() {
  const [tab, setTab] = useState('memo');

  return (
    <div className="tw1-fade-in h-full">
      <HeroBanner
        title="Truckwash 1 Memo"
        subtitle="AI-assistent voor memoriaalboekingen en tegenboeking generator"
        variant="default"
        icon={StickyNote}
        logos={[{ src: EXACT_LOGO, alt: 'Exact' }]}
      />

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit flex-wrap" data-testid="tw1-memo-tabs">
        <button data-testid="tab-memo" onClick={() => setTab('memo')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'memo' ? 'bg-white text-[#003366] shadow-sm' : 'text-[#526071] hover:text-[#0b1220]'}`}>
          <Bot className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> AI Memo
        </button>
        <button data-testid="tab-ic-correctie" onClick={() => setTab('ic-correctie')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'ic-correctie' ? 'bg-white text-[#003366] shadow-sm' : 'text-[#526071] hover:text-[#0b1220]'}`}>
          <ArrowRightLeft className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> IC Correctie
        </button>
        <button data-testid="tab-tegenboeking" onClick={() => setTab('tegenboeking')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'tegenboeking' ? 'bg-white text-[#003366] shadow-sm' : 'text-[#526071] hover:text-[#0b1220]'}`}>
          <RotateCcw className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Tegenboeking
        </button>
        <button data-testid="tab-spreidingstool" onClick={() => setTab('spreiding')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'spreiding' ? 'bg-white text-[#6366f1] shadow-sm' : 'text-[#526071] hover:text-[#0b1220]'}`}>
          <BarChart3 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Spreidingstool
        </button>
      </div>

      {tab === 'memo' && <AiMemoTab />}
      {tab === 'ic-correctie' && <IcCorrectieTab />}
      {tab === 'tegenboeking' && <TegenboekingTab />}
      {tab === 'spreiding' && <SpreidingstoolTab />}
    </div>
  );
}
